import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { publicApi, resolveImage } from '../api';
import { useLang } from '../i18n';
import PageMeta from '../components/PageMeta';

// Zone page (`/zone/:slug`) — lists every city belonging to the given
// macro-area (e.g. "Lago di Garda") using the same card style used on the
// homepage for standalone cities.
export default function ZonePage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [zone, setZone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicApi
      .zone(slug)
      .then((data) => { if (alive) { setZone(data); setError(''); } })
      .catch(() => { if (alive) setError('not_found'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const displayName = (zone && (zone.full_name?.[lang] || zone.full_name?.it || zone.name)) || '';
  const cities = zone?.cities || [];

  return (
    <div className="min-h-[80vh]" data-testid="zone-page">
      {zone && (
        <PageMeta
          title={`${displayName} — Città e destinazioni | YUP`}
          description={`Scopri tutte le città della zona ${displayName}: cosa vedere, ristoranti, esperienze e attività da non perdere.`}
        />
      )}
      {/* Hero */}
      <section className="relative w-full h-[38vh] sm:h-[48vh] overflow-hidden">
        {zone?.image ? (
          <img
            src={resolveImage(zone.image)}
            alt={displayName}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-neutral-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/80" />
        <div className="relative z-10 max-w-5xl mx-auto h-full flex flex-col justify-end px-4 sm:px-6 pb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 w-fit" data-testid="zone-back-home">
            <ArrowLeft size={16} /> Home
          </Link>
          <div className="text-[color:var(--yup-primary)] text-[11px] tracking-[0.3em] uppercase mb-2">
            YUP · Zona
          </div>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="zone-title"
          >
            {displayName || (loading ? '…' : 'Zona')}
          </h1>
          {!loading && !error && (
            <p className="mt-3 text-white/70 text-sm sm:text-base tracking-[0.2em] uppercase">
              {cities.length} {cities.length === 1 ? 'città' : 'città'}
            </p>
          )}
        </div>
      </section>

      {/* Cities grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-gray-400" size={32} />
          </div>
        )}
        {error === 'not_found' && (
          <div className="text-center text-white/70 py-16" data-testid="zone-not-found">
            <p className="text-lg">Zona non trovata.</p>
            <Link to="/" className="inline-block mt-4 text-[color:var(--yup-primary)] hover:underline">Torna alla home</Link>
          </div>
        )}
        {!loading && !error && cities.length === 0 && (
          <div className="text-center text-white/60 py-16">
            Nessuna città ancora assegnata a questa zona.
          </div>
        )}
        {!loading && cities.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8" data-testid="zone-cities-grid">
            {cities.map((city, idx) => (
              <div key={city.slug} className="fade-in-up" style={{ animationDelay: `${0.05 + idx * 0.08}s` }}>
                {city.status === 'active' ? (
                  <Link to={`/${city.slug}`} className="city-card block" data-testid={`zone-city-card-${city.slug}`}>
                    <img
                      src={resolveImage(city.image)}
                      alt={city.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-[160px] sm:h-[240px] md:h-[380px] object-cover"
                    />
                    <div className="overlay" />
                    <div className="label">{city.name}</div>
                  </Link>
                ) : (
                  <div className="city-card block relative cursor-not-allowed" data-testid={`zone-city-card-${city.slug}`}>
                    <img
                      src={resolveImage(city.image)}
                      alt={city.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-[160px] sm:h-[240px] md:h-[380px] object-cover filter grayscale-[35%]"
                    />
                    <div className="overlay" />
                    <div className="label">
                      {city.name}
                      <div className="soon-badge mt-2">{t('soon')}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
